import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ScrollText } from "lucide-react";
import { TERMS_LAST_UPDATED, SUPPORT_EMAIL } from "../lib/constants";

/**
 * Public terms-of-service page rendered at `/terms`. This is referenced
 * from App Store Connect (the standard Apple EULA is acceptable too, but a
 * custom URL is required if you depart from it in any way — we do, around
 * subscriptions and AI content).
 *
 * Plain-English boilerplate — have legal counsel review before launching
 * in regulated jurisdictions (EU consumer rights, AU consumer law, etc.).
 */
const Section = ({ title, children, testid }) => (
  <section className="mb-10" data-testid={testid}>
    <h2 className="font-display text-2xl sm:text-3xl text-white mb-3">{title}</h2>
    <div className="text-white/70 leading-relaxed space-y-3 text-sm sm:text-base">{children}</div>
  </section>
);

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-white/50 hover:text-white mb-8"
          data-testid="terms-back-home"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to home
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <ScrollText className="w-5 h-5 text-[#6B9BFF]" />
          <div className="label-eyebrow text-[#6B9BFF]">Terms of Service</div>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none mb-4">
          The fine print, in plain English.
        </h1>
        <p className="text-white/60 text-sm mb-12">
          Last updated: <span className="text-white/80">{TERMS_LAST_UPDATED}</span>
        </p>

        <Section title="1. Acceptance" testid="terms-acceptance">
          <p>
            By creating an account or using DAIONE, you agree to these Terms of Service. If you
            don't agree, please don't use the app.
          </p>
        </Section>

        <Section title="2. Your account" testid="terms-account">
          <p>You agree to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Provide accurate information when signing up.</li>
            <li>Keep your password secret. You're responsible for activity on your account.</li>
            <li>Notify us at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#6B9BFF] underline">{SUPPORT_EMAIL}</a> if you think your account has been compromised.</li>
            <li>Be at least 13 years old (or the minimum age in your country).</li>
          </ul>
        </Section>

        <Section title="3. Not medical advice" testid="terms-not-medical">
          <p>
            DAIONE is a fitness and nutrition tool. The AI-generated workouts, calorie targets,
            macro splits, and any health-related guidance are <strong className="text-white">informational
            only</strong> and not a substitute for advice from a qualified healthcare professional,
            registered dietitian, or certified personal trainer.
          </p>
          <p>
            Always consult a doctor before starting a new exercise or diet program, especially
            if you have a medical condition, are pregnant, or are recovering from an injury.
            Train at your own risk.
          </p>
        </Section>

        <Section title="4. Subscriptions, billing, and refunds" testid="terms-subscription">
          <p>
            DAIONE offers a free 7-day Pro trial, after which Pro features (unlimited AI plans,
            AI meal scanning, advanced analytics, badge sharing) are gated behind a paid
            subscription. Pricing is shown in-app at the point of purchase.
          </p>
          <p>
            <strong className="text-white">Web subscriptions</strong> are billed through Stripe. You
            can cancel at any time from <em>Billing → Manage</em>. Cancelling stops the next
            renewal — you keep Pro until the period ends. Refunds are at our discretion; email{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#6B9BFF] underline">{SUPPORT_EMAIL}</a>{" "}
            within 14 days of a charge if something looks wrong.
          </p>
          <p>
            <strong className="text-white">iOS subscriptions</strong> (when purchased via Apple's
            in-app purchase) are governed by Apple's standard EULA and refund process. Apple
            handles all billing, cancellation, and refunds; visit{" "}
            <em>Settings → [your name] → Subscriptions</em> on your device.
          </p>
        </Section>

        <Section title="5. AI-generated content" testid="terms-ai">
          <p>
            DAIONE uses third-party AI models (currently Anthropic Claude) to generate workout
            plans, calorie estimates from meal photos, and food breakdowns. AI output:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>May contain errors or inaccuracies.</li>
            <li>Is generated from your inputs — keep them accurate.</li>
            <li>Should not be the sole basis for medical, dietary, or fitness decisions.</li>
          </ul>
          <p>
            You may not use DAIONE to generate harmful, unsafe, or extreme dietary plans, nor
            re-sell the AI output as your own product or coaching service.
          </p>
        </Section>

        <Section title="6. Acceptable use" testid="terms-acceptable-use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Reverse engineer, decompile, or attempt to extract source code or model weights.</li>
            <li>Scrape, abuse rate limits, or use automated tools to consume the API.</li>
            <li>Upload illegal, harassing, or sexually explicit progress photos.</li>
            <li>Share your account with others (each account is single-user).</li>
            <li>Interfere with other users or the security of the service.</li>
          </ul>
        </Section>

        <Section title="7. Your content" testid="terms-content">
          <p>
            You retain ownership of every progress photo, workout log, and meal entry you upload.
            You grant us a limited licence to store and display this content back to you, as
            needed to operate the app. We do not use your content to train AI models.
          </p>
          <p>
            You can export or delete your content at any time from <em>Profile</em>.
          </p>
        </Section>

        <Section title="8. Third-party services" testid="terms-third-party">
          <p>
            DAIONE integrates with the following services. Your use of them is also governed by
            their terms:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong className="text-white">Spotify</strong> (optional music playback) — Spotify's Terms of Use.</li>
            <li><strong className="text-white">Stripe</strong> (web payments) — Stripe's Services Agreement.</li>
            <li><strong className="text-white">Anthropic</strong> (Claude AI) — Anthropic's Acceptable Use Policy.</li>
            <li><strong className="text-white">Apple</strong> (iOS purchases) — Apple Media Services Terms.</li>
          </ul>
        </Section>

        <Section title="9. Disclaimers & limitation of liability" testid="terms-disclaimer">
          <p>
            DAIONE is provided "as is" and "as available", without warranty of any kind, express
            or implied. To the maximum extent permitted by law, our total liability to you for
            any claim arising out of these terms is limited to the amount you paid us in the
            12 months preceding the claim, or USD $50, whichever is greater.
          </p>
          <p>
            Nothing in these terms excludes liability that cannot be excluded under your local
            consumer protection laws.
          </p>
        </Section>

        <Section title="10. Termination" testid="terms-termination">
          <p>
            You can close your account at any time. We may suspend or terminate your account if
            you breach these terms, abuse the service, or use it for fraud. We'll give notice
            where reasonably possible.
          </p>
        </Section>

        <Section title="11. Changes to these terms" testid="terms-changes">
          <p>
            We may update these terms occasionally. Material changes will be notified by email
            or in-app banner at least 14 days before they take effect. Continuing to use DAIONE
            after that means you accept the new terms.
          </p>
        </Section>

        <Section title="12. Governing law & contact" testid="terms-contact">
          <p>
            These terms are governed by the laws of the jurisdiction in which DAIONE Pty Ltd is
            incorporated (update before public launch). Questions? Email{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#6B9BFF] underline">{SUPPORT_EMAIL}</a>.
          </p>
        </Section>

        <div className="text-white/40 text-xs uppercase tracking-widest pt-8 border-t border-white/5">
          <Link to="/privacy" className="hover:text-white mr-6" data-testid="terms-link-privacy">Privacy</Link>
          <Link to="/support" className="hover:text-white" data-testid="terms-link-support">Support</Link>
        </div>
      </div>
    </div>
  );
}
